import { useEffect, useState, useRef, useMemo } from 'react';

import { useAzurite } from '@/config';
import { IFile } from '@/models';
import { getFolderNameFromUrl, getSasTokenFromFileUrl } from '@/services';
import { useFileStore, useToastStore } from '@/store';

export const useFetchSasToken = (files?: (IFile | undefined)[] | null) => {
  const { updateToast } = useToastStore();
  const [sasToken, setSasToken] = useState<string | undefined>(undefined);
  const [fileFetchFailed, setFileFetchFailed] = useState(false);

  // Cache to store previous results
  const cacheRef = useRef<Map<string, { sasToken: string; timestamp: number }>>(
    new Map(),
  );
  const lastFilesHashRef = useRef<string>('');

  // Create a stable hash of the files to detect actual changes
  const filesHash = useMemo(() => {
    if (!files?.length) return '';

    return files
      .filter(Boolean)
      .map((file) => `${file?._id || file?.url || ''}-${file?.updatedAt || ''}`)
      .sort()
      .join('|');
  }, [files]);

  const fetchSasToken = async () => {
    try {
      const fileWithUrl = files?.find((file) => file && !!file.url);

      if (!fileWithUrl?.url) {
        setSasToken(undefined);
        return;
      }

      // Get SAS token matched with folder name from file URL.
      const folderName = getFolderNameFromUrl(fileWithUrl.url);
      if (!folderName) {
        setSasToken(undefined);
        return;
      }

      // Create cache key based on file URL and folder
      const cacheKey = `${fileWithUrl.url}-${folderName}`;

      // Check cache first (with 5 minute expiry)
      const cached = cacheRef.current.get(cacheKey);
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;

      if (cached && cached.timestamp > fiveMinutesAgo) {
        setSasToken(cached.sasToken);
        setFileFetchFailed(false);
        return;
      }

      let fileSasToken: string | undefined = fileWithUrl.url.split('?')[1]; // get sas token if attached on url

      if (useAzurite !== 'true') {
        // not using azurite (i.e on dev/staging/prod)
        // get up-to-date valid token
        const { fileTokens, checkUploadTokenExpiration, fetchFileTokens } =
          useFileStore.getState();
        const isTokenExpired =
          !fileSasToken || checkUploadTokenExpiration(folderName);
        if (isTokenExpired) {
          // Wait for fetch to complete, even if multiple components are trying
          let newTokens;
          if (useFileStore.getState().fetching) {
            // If already fetching, wait for it to complete
            await new Promise((resolve) => {
              const checkFetching = () => {
                if (!useFileStore.getState().fetching) {
                  resolve(undefined);
                } else {
                  setTimeout(checkFetching, 100);
                }
              };
              checkFetching();
            });
            newTokens = useFileStore.getState().fileTokens || {};
          } else {
            newTokens = await fetchFileTokens();
          }
          fileSasToken = newTokens[folderName]?.sasToken ?? fileSasToken;
        } else {
          fileSasToken = fileTokens?.[folderName]?.sasToken ?? fileSasToken;
        }
      } else {
        // on local environment
        if (!fileSasToken) {
          fileSasToken = await getSasTokenFromFileUrl(fileWithUrl.url);
        }
      }

      if (fileSasToken) {
        // Cache the result
        cacheRef.current.set(cacheKey, {
          sasToken: fileSasToken,
          timestamp: Date.now(),
        });
        setSasToken(fileSasToken);
      } else {
        setSasToken(undefined);
      }

      setFileFetchFailed(false);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
      setFileFetchFailed(true);
    }
  };

  useEffect(() => {
    // Only fetch if files exist, and files actually changed
    if (
      !!files &&
      files?.length &&
      filesHash !== '' &&
      filesHash !== lastFilesHashRef.current
    ) {
      lastFilesHashRef.current = filesHash;
      fetchSasToken();
    }
  }, [filesHash, fileFetchFailed]); // Only depend on the hash, not the files array

  return sasToken;
};

export default useFetchSasToken;
